import type {
    IteroOrder,
    IteroOrderPage,
    IteroOrdersFilterParams,
    IteroOrdersParams
} from '../../../../services/iteroBrowse';
import { mergeUniquePages, pageOffset } from './browsePaging.ts';
import {
    DEFAULT_SCANNED_PRESET,
    customRangeError,
    scannedRange,
    type ScannedPreset
} from './filter-bar/chipPresets.ts';
import { hasSearchText, orderSearchChart } from './searchInput.ts';

const ORDERS_PAGE_SIZE = 50;
export const PATIENT_ORDERS_PAGE_SIZE = 50;

export interface OrderFilterValues {
    search: string;
    scanned: ScannedPreset;
    customFrom: string;
    customTo: string;
}

export const DEFAULT_ORDER_FILTERS: OrderFilterValues = {
    search: '',
    scanned: DEFAULT_SCANNED_PRESET,
    customFrom: '',
    customTo: ''
};

export function isDefaultOrderFilters(filters: OrderFilterValues): boolean {
    return !hasSearchText(filters.search) && filters.scanned === DEFAULT_SCANNED_PRESET;
}

export function orderFiltersError(filters: OrderFilterValues): string | null {
    return customRangeError(filters.scanned, { from: filters.customFrom, to: filters.customTo });
}

export function buildOrderFilterParams(
    filters: OrderFilterValues,
    today: string
): IteroOrdersFilterParams {
    const { dateFrom, dateTo } = scannedRange(
        filters.scanned,
        { from: filters.customFrom, to: filters.customTo },
        today
    );
    return {
        pageSize: ORDERS_PAGE_SIZE,
        dateFrom,
        dateTo,
        chartNumber: orderSearchChart(filters.search)
    };
}

export function orderPageParams(
    filterParams: IteroOrdersFilterParams,
    page: number
): IteroOrdersParams {
    return { ...filterParams, offset: pageOffset(page, filterParams.pageSize) };
}

export function patientOrdersParams(chartNumber: string): IteroOrdersFilterParams {
    return { pageSize: PATIENT_ORDERS_PAGE_SIZE, chartNumber: chartNumber.trim() };
}

export function nextOrdersOffset(page: IteroOrderPage): number | undefined {
    if (!page.hasMore || page.orders.length === 0) return undefined;
    return page.offset + page.orders.length;
}

export function mergeOrderPages(pages: readonly IteroOrderPage[]): IteroOrder[] {
    return mergeUniquePages(
        pages,
        (page) => page.orders,
        (order) => order.rxId
    );
}

const normalizedChart = (chartNumber: string) => chartNumber.trim().toLowerCase();

export function belongsToChart(order: IteroOrder, chartNumber: string): boolean {
    return normalizedChart(order.patientChartNumber) === normalizedChart(chartNumber);
}

// The chart filter is a "contains" match upstream, so keep only this patient's exact chart.
export function ordersForChart(orders: readonly IteroOrder[], chartNumber: string): IteroOrder[] {
    return orders.filter((order) => belongsToChart(order, chartNumber));
}
